import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Eye,
  Download,
  History,
  Plus,
  RefreshCw,
  FileText,
  Users,
  Calendar,
  User,
  Building,
  Briefcase,
  Tag,
  Clock,
  AlertCircle,
  Check,
  X,
  Search,
  UserPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Workflow steps
const STEPS = [
  { id: 1, name: 'Service Type', description: 'Select the case service type' },
  { id: 2, name: 'Upload Excel', description: 'Upload your spreadsheet' },
  { id: 3, name: 'Map Fields', description: 'Map participant identifiers and fields' },
  { id: 4, name: 'Create Note', description: 'Build your case note template' },
  { id: 5, name: 'Validate', description: 'Review validation results' },
  { id: 6, name: 'Preview', description: 'Preview merged case notes' },
  { id: 7, name: 'Confirm', description: 'Confirm and process' },
  { id: 8, name: 'Results', description: 'View processing results' }
];

// MYHELP system merge fields
const SYSTEM_MERGE_FIELDS = {
  participant: [
    { label: 'Participant Full Name', value: '{{participant_name}}' },
    { label: 'Participant Number', value: '{{participant_number}}' },
    { label: 'State ID Number', value: '{{state_id}}' },
    { label: 'Phone', value: '{{participant_phone}}' },
    { label: 'Email', value: '{{participant_email}}' }
  ],
  enrollment: [
    { label: 'Enrollment ID', value: '{{enrollment_id}}' },
    { label: 'Program Type', value: '{{program_type}}' },
    { label: 'Case Service Type', value: '{{service_type}}' },
    { label: 'Enrollment Start Date', value: '{{enrollment_start_date}}' }
  ],
  user: [
    { label: 'Logged-In User Name', value: '{{user_name}}' },
    { label: 'Current Date', value: '{{current_date}}' },
    { label: 'Case Manager Name', value: '{{case_manager_name}}' }
  ]
};

const MassCaseNoteEntryPage = () => {
  const { user } = useAuth();
  const textareaRef = useRef(null);
  const subjectInputRef = useRef(null);
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Enrollment Type (was Service Type)
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  const [selectedEnrollmentType, setSelectedEnrollmentType] = useState('');
  
  // Step 2: Excel Upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Step 3: Field Mapping
  const [participantMatchField, setParticipantMatchField] = useState('');
  const [participantMatchColumn, setParticipantMatchColumn] = useState('');
  const [excelMergeFields, setExcelMergeFields] = useState([]);
  
  // Step 4: Case Note Template
  const [noteSubject, setNoteSubject] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteDateOption, setNoteDateOption] = useState('batch'); // 'batch' or 'excel'
  const [batchDate, setBatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [excelDateColumn, setExcelDateColumn] = useState('');
  // Scheduling options
  const [scheduleNote, setScheduleNote] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  
  // Step 5: Validation
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Step 6: Preview
  const [previewData, setPreviewData] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  
  // Step 7: Confirm
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processValidOnly, setProcessValidOnly] = useState(false);
  
  // Step 8: Results
  const [processingResults, setProcessingResults] = useState(null);
  
  // Batch History
  const [showHistory, setShowHistory] = useState(false);
  const [batchHistory, setBatchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Participant Resolution (for "No matching participant found" errors)
  const [showParticipantSearchDialog, setShowParticipantSearchDialog] = useState(false);
  const [resolvingErrorRow, setResolvingErrorRow] = useState(null);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [participantSearchResults, setParticipantSearchResults] = useState([]);
  const [searchingParticipants, setSearchingParticipants] = useState(false);
  const [resolvedErrors, setResolvedErrors] = useState({}); // Map of row -> resolved participant
  
  // Available participant match fields
  const participantMatchOptions = [
    { value: 'state_id', label: 'State ID Number' },
    { value: 'participant_number', label: 'Participant Number' },
    { value: 'full_name', label: 'Full Name' },
    { value: 'ssn_last4', label: 'SSN Last 4 Digits' }
  ];
  
  useEffect(() => {
    fetchEnrollmentTypes();
  }, []);
  
  const fetchEnrollmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      const types = response.data.enrollment_types || response.data || [];
      const activeTypes = types.filter(et => et.is_active);
      setEnrollmentTypes(activeTypes);
    } catch (error) {
      console.error('Failed to load enrollment types:', error);
      toast.error('Failed to load enrollment types');
    }
  };
  
  const fetchBatchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/mass-case-notes/history`);
      setBatchHistory(response.data.batches || []);
    } catch (error) {
      toast.error('Failed to load batch history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Participant search for resolving "No matching participant found" errors
  const searchParticipantsForResolution = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setParticipantSearchResults([]);
      return;
    }
    setSearchingParticipants(true);
    try {
      const response = await axios.get(`${API}/admin/mass-upload/participant-search`, {
        params: { query }
      });
      setParticipantSearchResults(response.data.participants || []);
    } catch (error) {
      console.error('Participant search failed:', error);
      setParticipantSearchResults([]);
    } finally {
      setSearchingParticipants(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!showParticipantSearchDialog) return;
    const debounce = setTimeout(() => searchParticipantsForResolution(participantSearchQuery), 300);
    return () => clearTimeout(debounce);
  }, [participantSearchQuery, showParticipantSearchDialog, searchParticipantsForResolution]);

  // Open participant search dialog for a specific error row
  const openParticipantResolutionDialog = (errorRow) => {
    setResolvingErrorRow(errorRow);
    setParticipantSearchQuery(errorRow.identifier || '');
    setParticipantSearchResults([]);
    setShowParticipantSearchDialog(true);
  };

  // Handle participant selection for error resolution
  const handleParticipantSelection = (participant) => {
    if (!resolvingErrorRow) return;
    
    const rowIndex = resolvingErrorRow.row;
    
    // Add to resolved errors map
    const newResolved = {
      ...resolvedErrors,
      [rowIndex]: {
        participant_id: participant.id,
        participant_name: participant.name,
        original_identifier: resolvingErrorRow.identifier,
        original_error: resolvingErrorRow.error
      }
    };
    setResolvedErrors(newResolved);
    
    // Update validation results to reflect the resolution
    if (validationResults) {
      // Remove this error from error_details
      const newErrorDetails = validationResults.error_details.filter(err => err.row !== rowIndex);
      
      // Update counts
      const newValidationResults = {
        ...validationResults,
        error_details: newErrorDetails,
        errors: newErrorDetails.length,
        ready_to_create: validationResults.ready_to_create + 1
      };
      setValidationResults(newValidationResults);
      
      // Also update the preview data to include this resolved row
      if (previewData && excelData?.rows) {
        const rowData = excelData.rows[rowIndex - 2]; // Row index is 1-based + header row
        if (rowData) {
          // Build a preview note for this resolved row
          const resolvedPreviewNote = {
            row: rowIndex,
            participant_id: participant.id,
            participant_name: participant.name,
            subject: noteSubject,
            body: noteBody,
            resolved_manually: true
          };
          setPreviewData(prev => [...prev, resolvedPreviewNote]);
        }
      }
    }
    
    // Close dialog
    setShowParticipantSearchDialog(false);
    setResolvingErrorRow(null);
    setParticipantSearchQuery('');
    setParticipantSearchResults([]);
    
    toast.success(`Row ${rowIndex} resolved: ${participant.name}`);
  };

  // Close participant search dialog
  const closeParticipantResolutionDialog = () => {
    setShowParticipantSearchDialog(false);
    setResolvingErrorRow(null);
    setParticipantSearchQuery('');
    setParticipantSearchResults([]);
  };

  // Check if an error is a "No matching participant found" error that can be resolved
  const isParticipantNotFoundError = (error) => {
    return error && error.toLowerCase().includes('no matching participant');
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload an .xlsx Excel file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('enrollment_type', selectedEnrollmentType);
    
    try {
      const response = await axios.post(`${API}/mass-case-notes/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedFile(file);
      setUploadedFileName(file.name);
      setExcelData(response.data);
      setExcelHeaders(response.data.headers || []);
      
      // Create merge fields from Excel columns
      const mergeFields = (response.data.headers || []).map(header => ({
        label: header,
        value: `{{excel_${header.toLowerCase().replace(/[^a-z0-9]/g, '_')}}}`
      }));
      setExcelMergeFields(mergeFields);
      
      toast.success(`Excel file uploaded: ${response.data.row_count} rows found`);
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const insertMergeField = (field, target = 'body') => {
    if (target === 'subject' && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newValue = noteSubject.slice(0, start) + field + noteSubject.slice(end);
      setNoteSubject(newValue);
      // Set cursor position after inserted field
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + field.length, start + field.length);
      }, 0);
    } else if (target === 'body' && textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = noteBody.slice(0, start) + field + noteBody.slice(end);
      setNoteBody(newValue);
      // Set cursor position after inserted field
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.length, start + field.length);
      }, 0);
    }
  };
  
  const handleValidate = async () => {
    setValidating(true);
    try {
      const payload = {
        enrollment_type: selectedEnrollmentType,
        upload_id: excelData?.upload_id,
        participant_match_field: participantMatchField,
        participant_match_column: participantMatchColumn,
        note_subject: noteSubject,
        note_body: noteBody,
        date_option: noteDateOption,
        batch_date: noteDateOption === 'batch' ? batchDate : null,
        excel_date_column: noteDateOption === 'excel' ? excelDateColumn : null
      };
      
      const response = await axios.post(`${API}/mass-case-notes/validate`, payload);
      setValidationResults(response.data);
      setPreviewData(response.data.preview_notes || []);
      setCurrentStep(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };
  
  const handleProcess = async () => {
    setProcessing(true);
    try {
      // Convert resolved errors to the format expected by backend
      const manualResolutions = Object.entries(resolvedErrors).map(([row, data]) => ({
        row: parseInt(row),
        participant_id: data.participant_id,
        participant_name: data.participant_name
      }));
      
      const payload = {
        enrollment_type: selectedEnrollmentType,
        upload_id: excelData?.upload_id,
        participant_match_field: participantMatchField,
        participant_match_column: participantMatchColumn,
        note_subject: noteSubject,
        note_body: noteBody,
        date_option: noteDateOption,
        batch_date: noteDateOption === 'batch' ? batchDate : null,
        excel_date_column: noteDateOption === 'excel' ? excelDateColumn : null,
        process_valid_only: processValidOnly,
        manual_resolutions: manualResolutions.length > 0 ? manualResolutions : undefined,
        // Scheduling parameters
        schedule_note: scheduleNote,
        scheduled_datetime: scheduleNote && scheduledDateTime ? scheduledDateTime : null
      };
      
      const response = await axios.post(`${API}/mass-case-notes/process`, payload);
      setProcessingResults(response.data);
      setConfirmDialogOpen(false);
      setCurrentStep(8);
      if (response.data.scheduled) {
        toast.success(`Successfully scheduled ${response.data.total_rows} case notes for ${new Date(response.data.scheduled_datetime).toLocaleString()}`);
      } else {
        toast.success(`Successfully created ${response.data.created} case notes`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };
  
  const downloadResultsExcel = async () => {
    if (!processingResults?.batch_id) return;
    
    try {
      const response = await axios.get(
        `${API}/mass-case-notes/results/${processingResults.batch_id}/export`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Mass_Case_Note_Results_${processingResults.batch_id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download results');
    }
  };
  
  const resetWorkflow = () => {
    setCurrentStep(1);
    setSelectedEnrollmentType('');
    setUploadedFile(null);
    setUploadedFileName('');
    setExcelData(null);
    setExcelHeaders([]);
    setParticipantMatchField('');
    setParticipantMatchColumn('');
    setExcelMergeFields([]);
    setNoteSubject('');
    setNoteBody('');
    setNoteDateOption('batch');
    setBatchDate(new Date().toISOString().split('T')[0]);
    setExcelDateColumn('');
    setValidationResults(null);
    setPreviewData([]);
    setPreviewIndex(0);
    setProcessingResults(null);
    setProcessValidOnly(false);
    setResolvedErrors({}); // Clear resolved errors
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!selectedEnrollmentType;
      case 2:
        return !!excelData;
      case 3:
        return !!participantMatchField && !!participantMatchColumn;
      case 4:
        // If scheduling is enabled, require scheduledDateTime
        const dateValid = noteDateOption === 'batch' ? !!batchDate : !!excelDateColumn;
        const scheduleValid = scheduleNote ? !!scheduledDateTime : true;
        return !!noteSubject && !!noteBody && dateValid && scheduleValid;
      case 5:
        return validationResults && validationResults.ready_to_create > 0;
      case 6:
        return previewData.length > 0;
      default:
        return true;
    }
  };
  
  const goToNextStep = () => {
    if (currentStep === 4) {
      handleValidate();
    } else if (currentStep < 8) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Render step progress
  const renderStepProgress = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep === step.id
                    ? 'bg-primary text-primary-foreground'
                    : currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
              </div>
              <span className={`mt-2 text-xs text-center max-w-[80px] ${
                currentStep === step.id ? 'text-primary font-medium' : 'text-muted-foreground'
              }`}>
                {step.name}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${
                currentStep > step.id ? 'bg-green-500' : 'bg-muted'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
  
  // Render Step 1: Enrollment Type Selection
  const renderEnrollmentTypeStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="w-5 h-5" />
          Select Enrollment Type
        </CardTitle>
        <CardDescription>
          All participants in your spreadsheet must have an enrollment with this type
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md">
          <Label>Enrollment Type</Label>
          <Select value={selectedEnrollmentType} onValueChange={setSelectedEnrollmentType}>
            <SelectTrigger data-testid="enrollment-type-select">
              <SelectValue placeholder="Select an enrollment type..." />
            </SelectTrigger>
            <SelectContent>
              {enrollmentTypes.map((et) => (
                <SelectItem key={et.id} value={et.id}>
                  {et.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedEnrollmentType && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Selected:</strong> {enrollmentTypes.find(et => et.id === selectedEnrollmentType)?.name}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  // Render Step 2: Excel Upload
  const renderUploadStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" />
          Upload Excel Spreadsheet
        </CardTitle>
        <CardDescription>
          Upload an .xlsx file with participant data. Each row represents one case note.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              uploading ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary'
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Processing file...</p>
              </div>
            ) : uploadedFile ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
                <p className="font-medium">{uploadedFileName}</p>
                <p className="text-sm text-muted-foreground">
                  {excelData?.row_count} rows, {excelHeaders.length} columns
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUploadedFile(null);
                    setExcelData(null);
                    setExcelHeaders([]);
                  }}
                >
                  Upload Different File
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="excel-file-input"
                />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="font-medium">Click to upload or drag and drop</p>
                  <p className="text-sm text-muted-foreground">.xlsx files only</p>
                </div>
              </label>
            )}
          </div>
          
          {excelHeaders.length > 0 && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Detected Columns:</p>
              <div className="flex flex-wrap gap-2">
                {excelHeaders.map((header, idx) => (
                  <Badge key={idx} variant="secondary">{header}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  
  // Render Step 3: Field Mapping
  const renderFieldMappingStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Map Participant Identifier
        </CardTitle>
        <CardDescription>
          Select how participants in your spreadsheet will be matched to MYHELP records
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Participant Matching Field</Label>
            <Select value={participantMatchField} onValueChange={setParticipantMatchField}>
              <SelectTrigger data-testid="match-field-select">
                <SelectValue placeholder="Select identifier type..." />
              </SelectTrigger>
              <SelectContent>
                {participantMatchOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The MYHELP field to match against
            </p>
          </div>
          
          <div className="space-y-2">
            <Label>Excel Column</Label>
            <Select value={participantMatchColumn} onValueChange={setParticipantMatchColumn}>
              <SelectTrigger data-testid="match-column-select">
                <SelectValue placeholder="Select Excel column..." />
              </SelectTrigger>
              <SelectContent>
                {excelHeaders.map((header) => (
                  <SelectItem key={header} value={header}>
                    {header}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The column in your spreadsheet containing this identifier
            </p>
          </div>
        </div>
        
        {participantMatchField && participantMatchColumn && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Mapping:</strong> Excel column &quot;{participantMatchColumn}&quot; will be matched to MYHELP {participantMatchOptions.find(o => o.value === participantMatchField)?.label}
            </p>
          </div>
        )}
        
        <div className="mt-6">
          <h4 className="font-medium mb-2">Available Excel Merge Fields</h4>
          <p className="text-sm text-muted-foreground mb-3">
            These fields from your spreadsheet can be used in the case note template:
          </p>
          <div className="flex flex-wrap gap-2">
            {excelMergeFields.map((field, idx) => (
              <Badge key={idx} variant="outline" className="font-mono text-xs">
                {field.value}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  
  // Render Step 4: Case Note Template
  const renderNoteTemplateStep = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Case Note Template
            </CardTitle>
            <CardDescription>
              Create your case note template. Merge fields will be replaced with individual participant data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Case Note Subject</Label>
              <Input
                ref={subjectInputRef}
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                placeholder="Enter subject (merge fields allowed)..."
                data-testid="note-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Case Note Body</Label>
              <Textarea
                ref={textareaRef}
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="Enter your case note template..."
                rows={12}
                className="font-mono text-sm"
                data-testid="note-body-textarea"
              />
            </div>
            
            <div className="space-y-4 border-t pt-4">
              <Label>Case Note Date</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateOption"
                    checked={noteDateOption === 'batch'}
                    onChange={() => setNoteDateOption('batch')}
                  />
                  <span className="text-sm">One date for entire batch</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateOption"
                    checked={noteDateOption === 'excel'}
                    onChange={() => setNoteDateOption('excel')}
                  />
                  <span className="text-sm">Use date from Excel column</span>
                </label>
              </div>
              
              {noteDateOption === 'batch' ? (
                <Input
                  type="date"
                  value={batchDate}
                  onChange={(e) => setBatchDate(e.target.value)}
                  className="max-w-xs"
                  data-testid="batch-date-input"
                />
              ) : (
                <Select value={excelDateColumn} onValueChange={setExcelDateColumn}>
                  <SelectTrigger className="max-w-xs" data-testid="date-column-select">
                    <SelectValue placeholder="Select date column..." />
                  </SelectTrigger>
                  <SelectContent>
                    {excelHeaders.map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Scheduling Option */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="schedule-note"
                  checked={scheduleNote}
                  onCheckedChange={setScheduleNote}
                  data-testid="schedule-note-checkbox"
                />
                <Label htmlFor="schedule-note" className="cursor-pointer flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Schedule for a future date and time
                </Label>
              </div>
              
              {scheduleNote && (
                <div className="ml-6 space-y-2">
                  <Label>Scheduled Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="max-w-xs"
                    data-testid="scheduled-datetime-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    The case notes will be automatically posted at this date and time.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="space-y-4">
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Insert Merge Field</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="participant" className="w-full">
              <TabsList className="grid w-full grid-cols-4 text-xs">
                <TabsTrigger value="participant">Participant</TabsTrigger>
                <TabsTrigger value="enrollment">Case</TabsTrigger>
                <TabsTrigger value="user">System</TabsTrigger>
                <TabsTrigger value="excel">Excel</TabsTrigger>
              </TabsList>
              
              <TabsContent value="participant" className="mt-3">
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {SYSTEM_MERGE_FIELDS.participant.map((field) => (
                      <Button
                        key={field.value}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => insertMergeField(field.value)}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        {field.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="enrollment" className="mt-3">
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {SYSTEM_MERGE_FIELDS.enrollment.map((field) => (
                      <Button
                        key={field.value}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => insertMergeField(field.value)}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        {field.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="user" className="mt-3">
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {SYSTEM_MERGE_FIELDS.user.map((field) => (
                      <Button
                        key={field.value}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => insertMergeField(field.value)}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        {field.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="excel" className="mt-3">
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {excelMergeFields.map((field) => (
                      <Button
                        key={field.value}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => insertMergeField(field.value)}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        {field.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <Card className="card-shadow bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Tip:</strong> Click where you want to insert the field, then click a merge field button to insert it at that position.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
  
  // Render Step 5: Validation Results
  const renderValidationStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          Validation Results
        </CardTitle>
        <CardDescription>
          Review the validation status before creating case notes
        </CardDescription>
      </CardHeader>
      <CardContent>
        {validating ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating spreadsheet data...</p>
          </div>
        ) : validationResults ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-muted">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold">{validationResults.total_rows}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{validationResults.ready_to_create}</p>
                  <p className="text-sm text-green-600">Ready to Create</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{validationResults.warnings || 0}</p>
                  <p className="text-sm text-yellow-600">Warnings</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{validationResults.errors || 0}</p>
                  <p className="text-sm text-red-600">Errors</p>
                </CardContent>
              </Card>
            </div>
            
            {validationResults.error_details && validationResults.error_details.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2 text-red-600">
                  <XCircle className="w-4 h-4" />
                  Validation Errors
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (Resolve &quot;No matching participant&quot; errors by selecting the correct participant)
                  </span>
                </h4>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Identifier</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead className="w-40">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validationResults.error_details.slice(0, 20).map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.identifier || '-'}</TableCell>
                          <TableCell className="text-red-600">{err.error}</TableCell>
                          <TableCell>
                            {isParticipantNotFoundError(err.error) ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openParticipantResolutionDialog(err)}
                                className="text-primary hover:text-primary"
                                data-testid={`select-participant-row-${err.row}`}
                              >
                                <UserPlus className="w-4 h-4 mr-1" />
                                Select Participant
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {validationResults.error_details.length > 20 && (
                  <p className="text-sm text-muted-foreground">
                    Showing first 20 of {validationResults.error_details.length} errors
                  </p>
                )}
              </div>
            )}
            
            {/* Show resolved errors summary */}
            {Object.keys(resolvedErrors).length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  Manually Resolved ({Object.keys(resolvedErrors).length} rows)
                </h4>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Original Identifier</TableHead>
                        <TableHead>Resolved To</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(resolvedErrors).map(([row, data]) => (
                        <TableRow key={row}>
                          <TableCell>{row}</TableCell>
                          <TableCell className="text-muted-foreground">{data.original_identifier || '-'}</TableCell>
                          <TableCell className="text-green-600 font-medium">{data.participant_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {validationResults.ready_to_create === 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200">
                <p className="text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  No valid rows found. Please fix the errors and try again.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
  
  // Render Step 6: Preview
  const renderPreviewStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview Case Notes
            </CardTitle>
            <CardDescription>
              Review the fully merged case notes before creating them
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewIndex(Math.max(0, previewIndex - 1))}
              disabled={previewIndex === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {previewIndex + 1} of {previewData.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewIndex(Math.min(previewData.length - 1, previewIndex + 1))}
              disabled={previewIndex >= previewData.length - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {previewData.length > 0 && previewData[previewIndex] && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">Participant</p>
                <p className="font-medium">{previewData[previewIndex].participant_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Identifier</p>
                <p className="font-medium">{previewData[previewIndex].identifier}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Case</p>
                <p className="font-medium font-mono text-sm">{previewData[previewIndex].case_id}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{previewData[previewIndex].note_date}</p>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-1">Subject</p>
                <p className="font-medium">{previewData[previewIndex].merged_subject}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Case Note</p>
                <div className="whitespace-pre-wrap text-sm bg-muted p-4 rounded">
                  {previewData[previewIndex].merged_body}
                </div>
              </div>
            </div>
            
            <Badge variant={previewData[previewIndex].valid ? 'default' : 'destructive'}>
              {previewData[previewIndex].valid ? 'Valid' : 'Invalid'}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  // Render Step 7: Confirm
  const renderConfirmStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          Confirm Case Note Creation
        </CardTitle>
        <CardDescription>
          Please review and confirm before creating case notes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200">
          <p className="text-lg text-center">
            You are about to create <strong>{validationResults?.ready_to_create || 0}</strong> individual Case Notes across <strong>{validationResults?.ready_to_create || 0}</strong> participant Cases.
          </p>
          <p className="text-center text-muted-foreground mt-2">
            Please confirm that you have reviewed the information and that the documentation is accurate.
          </p>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="processValidOnly"
              checked={processValidOnly}
              onCheckedChange={setProcessValidOnly}
            />
            <label htmlFor="processValidOnly" className="text-sm">
              Process Valid Rows Only (skip rows with errors)
            </label>
          </div>
          
          {!processValidOnly && validationResults?.errors > 0 && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200">
              <p className="text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                There are {validationResults.errors} rows with errors. Enable &quot;Process Valid Rows Only&quot; or go back to fix errors.
              </p>
            </div>
          )}
        </div>
        
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => setCurrentStep(6)}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Preview
          </Button>
          <Button 
            onClick={() => setConfirmDialogOpen(true)}
            disabled={!processValidOnly && validationResults?.errors > 0}
          >
            Create Case Notes
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
  
  // Render Step 8: Results
  const renderResultsStep = () => (
    <Card className="card-shadow">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Processing Complete
            </CardTitle>
            <CardDescription>
              Batch ID: {processingResults?.batch_id}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadResultsExcel}>
              <Download className="w-4 h-4 mr-2" />
              Download Results
            </Button>
            <Button onClick={resetWorkflow}>
              <RefreshCw className="w-4 h-4 mr-2" />
              New Import
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {processingResults && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-muted">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold">{processingResults.total_rows}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{processingResults.created}</p>
                  <p className="text-sm text-green-600">Case Notes Created</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{processingResults.skipped}</p>
                  <p className="text-sm text-yellow-600">Skipped</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{processingResults.failed}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </CardContent>
              </Card>
            </div>
            
            {processingResults.created_notes && processingResults.created_notes.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Created Case Notes</h4>
                <div className="rounded-md border overflow-x-auto max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Participant</TableHead>
                        <TableHead>Case ID</TableHead>
                        <TableHead>Note ID</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {processingResults.created_notes.map((note, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{note.row}</TableCell>
                          <TableCell>{note.participant_name}</TableCell>
                          <TableCell className="font-mono text-sm">{note.case_id}</TableCell>
                          <TableCell className="font-mono text-sm">{note.note_id}</TableCell>
                          <TableCell>
                            <Badge variant="default">Created</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  // Render Batch History
  const renderHistory = () => (
    <Card className="card-shadow">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Mass Case Note History
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowHistory(false)}>
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loadingHistory ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : batchHistory.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No batch history found</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Enrollment Type</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchHistory.map((batch) => (
                  <TableRow key={batch.id} className="cursor-pointer hover:bg-muted" onClick={() => setSelectedBatch(batch)}>
                    <TableCell className="font-mono text-sm">{batch.id}</TableCell>
                    <TableCell>{formatDateTime(batch.created_at)}</TableCell>
                    <TableCell>{batch.user_name}</TableCell>
                    <TableCell>{batch.enrollment_type_name || batch.service_type_name}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{batch.file_name}</TableCell>
                    <TableCell>{batch.total_rows}</TableCell>
                    <TableCell className="text-green-600">{batch.created}</TableCell>
                    <TableCell className="text-red-600">{batch.failed}</TableCell>
                    <TableCell>
                      <Badge variant={batch.status === 'completed' ? 'default' : 'secondary'}>
                        {batch.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  // Main render
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Mass Case Note Entry</h1>
          <p className="text-muted-foreground">Create case notes for multiple participants using Excel import</p>
        </div>
        <Button variant="outline" onClick={() => { setShowHistory(true); fetchBatchHistory(); }}>
          <History className="w-4 h-4 mr-2" />
          View History
        </Button>
      </div>
      
      {showHistory ? (
        renderHistory()
      ) : (
        <>
          {renderStepProgress()}
          
          {currentStep === 1 && renderEnrollmentTypeStep()}
          {currentStep === 2 && renderUploadStep()}
          {currentStep === 3 && renderFieldMappingStep()}
          {currentStep === 4 && renderNoteTemplateStep()}
          {currentStep === 5 && renderValidationStep()}
          {currentStep === 6 && renderPreviewStep()}
          {currentStep === 7 && renderConfirmStep()}
          {currentStep === 8 && renderResultsStep()}
          
          {currentStep < 8 && (
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={goToPreviousStep}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              <Button
                onClick={goToNextStep}
                disabled={!canProceed() || validating}
              >
                {validating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {currentStep === 4 ? 'Validate' : 'Next'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Case Note Creation</DialogTitle>
            <DialogDescription>
              This action will create {validationResults?.ready_to_create || 0} case notes. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleProcess} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Create Case Notes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Participant Search Dialog for Error Resolution */}
      <Dialog open={showParticipantSearchDialog} onOpenChange={setShowParticipantSearchDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Select Participant
            </DialogTitle>
            <DialogDescription>
              Search for and select the correct participant for Row {resolvingErrorRow?.row}
              {resolvingErrorRow?.identifier && (
                <span className="block mt-1 text-xs">
                  Original identifier: <span className="font-mono">{resolvingErrorRow.identifier}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={participantSearchQuery}
                  onChange={(e) => setParticipantSearchQuery(e.target.value)}
                  className="pl-9"
                  autoFocus
                  data-testid="participant-search-input"
                />
              </div>
              {searchingParticipants && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </div>
            
            {participantSearchQuery.length > 0 && participantSearchQuery.length < 2 && (
              <p className="text-sm text-muted-foreground">Type at least 2 characters to search</p>
            )}
            
            {participantSearchResults.length > 0 ? (
              <ScrollArea className="h-[250px] rounded-md border">
                <div className="p-2 space-y-1">
                  {participantSearchResults.map((participant) => (
                    <button
                      key={participant.id}
                      onClick={() => handleParticipantSelection(participant)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center justify-between group"
                      data-testid={`select-participant-${participant.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{participant.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground group-hover:text-primary">
                        {participant.id}
                      </span>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            ) : participantSearchQuery.length >= 2 && !searchingParticipants ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No participants found matching &quot;{participantSearchQuery}&quot;</p>
              </div>
            ) : null}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeParticipantResolutionDialog}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MassCaseNoteEntryPage;
